import React, { useEffect } from "react";
import {
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  IconButton,
  Tooltip,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { MdEdit, MdDelete, MdAdd, MdSearch } from "react-icons/md";
import Card from "components/card/Card";
import CardForm from "./CardForm";
import {
  fetchCards,
  addCard,
  updateCard,
  deleteCard,
} from "redux/slices/cardSlice";

export default function CardsTable() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const dispatch = useDispatch();
  const { items: cards, pagination, loading } = useSelector((state) => state.cards);
  const [selectedCard, setSelectedCard] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [searchInput, setSearchInput] = React.useState("");
  const [search, setSearch] = React.useState("");
  const toast = useToast();

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      setSearch(searchInput);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    dispatch(fetchCards({ page, limit: 20, search }));
  }, [dispatch, page, search]);

  const handleAdd = () => {
    setSelectedCard(null);
    onOpen();
  };

  const handleEdit = (card) => {
    const mappedCard = {
      cardId: card.cardId,
      description: card.description,
      title: card.title,
      name: card.name,
      scientificIdentity: card.scientificIdentity,
      tailNumber: card.tailNumber,
      cardFrontImage: card.cardFrontImage,
      cardBackImage: card.cardBackImage,
      invertMaterials: card.invertMaterials ?? false,
      foilMask: card.foilMask || "",
    };
    setSelectedCard(mappedCard);
    onOpen();
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this card?")) {
      try {
        await dispatch(deleteCard(id));
        toast({
          title: "Card deleted successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        const result = await dispatch(fetchCards({ page, limit: 20, search })).unwrap();
        if (result.aircrafts.length === 0 && page > 1) {
          setPage(page - 1);
        }
      } catch (error) {
        toast({
          title: "Error deleting card",
          description: error.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleSubmit = async (formData) => {
    try {
      if (selectedCard) {
        console.log("🚀 ~ handleSubmit ~ selectedCard:", selectedCard);
        await dispatch(updateCard({ id: selectedCard.cardId, cardData: formData }));
        dispatch(fetchCards({ page, limit: 20, search }));
        onClose();
        toast({
          title: "Card updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        console.log("🚀 ~ handleSubmit ~ formData:", formData);
        await dispatch(addCard(formData));
        setPage(1);
        dispatch(fetchCards({ page: 1, limit: 20, search }));
        onClose();
        toast({
          title: "Card added successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.log("🚀 ~ handleSubmit ~ error:", error);
      toast({
        title: selectedCard ? "Error updating card" : "Error adding card",
        description: error.message || "An error occurred",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Card
      direction="column"
      w="100%"
      px="0px"
      overflowX={{ sm: "scroll", lg: "hidden" }}
    >
      <Flex px="25px" justify="space-between" mb="20px" align="center">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Cards Management
        </Text>
        <Button
          leftIcon={<MdAdd />}
          onClick={handleAdd}
          background="linear-gradient(100.88deg, #463C8F -1.15%, #CE0C88 177.18%)"
          color="white"
          _hover={{
            background:
              "linear-gradient(100.88deg, #463C8F -1.15%, #CE0C88 177.18%)",
            opacity: 0.9,
          }}
        >
          Add New Card
        </Button>
      </Flex>

      <Flex px="25px" mb="20px">
        <InputGroup maxW="320px">
          <InputLeftElement pointerEvents="none">
            <MdSearch color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search by Card ID, Title, Name, Scientific Identity, or Tail Number"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </InputGroup>
      </Flex>

      <Table variant="simple" color="gray.500" mb="24px">
        <Thead>
          <Tr>
            <Th borderColor={borderColor}>Card ID</Th>
            <Th borderColor={borderColor}>Title</Th>
            <Th borderColor={borderColor}>Name</Th>
            <Th borderColor={borderColor}>Scientific Identity</Th>
            <Th borderColor={borderColor}>Tail Number</Th>
            <Th borderColor={borderColor}>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {cards && cards.length === 0 && (
            <Tr>
              <Td colSpan={6} textAlign="center" color="gray.500">
                {search ? "No cards match your search." : "No cards found."}
              </Td>
            </Tr>
          )}
          {cards &&
            cards.length > 0 &&
            cards.map((card) => (
              <Tr key={card.cardId}>
                <Td>{card.cardId}</Td>
                <Td>{card.title}</Td>
                <Td>{card.name}</Td>
                <Td>{card.scientificIdentity}</Td>
                <Td>{card.tailNumber}</Td>
                <Td>
                  <Flex gap={2}>
                    <Tooltip label="Edit">
                      <IconButton
                        icon={<MdEdit />}
                        onClick={() => handleEdit(card)}
                        colorScheme="blue"
                        variant="ghost"
                      />
                    </Tooltip>
                    <Tooltip label="Delete">
                      <IconButton
                        icon={<MdDelete />}
                        onClick={() => handleDelete(card.cardId)}
                        colorScheme="red"
                        variant="ghost"
                      />
                    </Tooltip>
                  </Flex>
                </Td>
              </Tr>
            ))}
        </Tbody>
      </Table>

      <Flex px="25px" justify="space-between" align="center" mb="10px">
        <Text fontSize="sm" color="gray.500">
          Page {pagination?.currentPage ?? 1} of {pagination?.totalPages ?? 1} (
          {pagination?.totalItems ?? 0} total cards)
        </Text>
        <Flex gap={2}>
          <Button
            size="sm"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            isDisabled={page <= 1 || loading}
          >
            Previous
          </Button>
          <Button
            size="sm"
            onClick={() => setPage((prev) => prev + 1)}
            isDisabled={page >= (pagination?.totalPages ?? 1) || loading}
          >
            Next
          </Button>
        </Flex>
      </Flex>

      <CardForm
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={handleSubmit}
        initialData={selectedCard}
      />
    </Card>
  );
}
